/* ============
 * Account
 * ============
 *
 * This is the page for editing account.
 */
import { mapGetters } from 'vuex';
import Forms from './../../../../utils/forms/forms';
import accountService from './../../../../services/account';
import accountTransformer from './../../../../transformers/account';
import authService from './../../../../services/auth';
import countryService from './../../../../services/country';
import siteLanguagesService from './../../../../services/siteLanguages';
import organizationTypeService from './../../../../services/organizationType';
import passwordTransformer from './../../../../transformers/password';
import postalService from './../../../../services/postal';
import publicProfileService from './../../../../services/publicProfile';
import publicProfileTransformer from './../../../../transformers/publicProfile';
import Vue from 'vue';

export default {
  data() {
    return {
      organizationForm: new Forms({
        organizationTypeId: {
          value: '',
          type: 'select',
        },
        organizationName: {
          value: '',
          type: 'text',
        },
        firstName: {
          value: '',
          type: 'text',
        },
        lastName: {
          value: '',
          type: 'text',
        },
        organizationDescription: {
          value: '',
          type: 'textarea',
        },
        organizationProfilePicture: {
          value: '',
          type: 'file',
        },
      }),
      contactInformationForm: new Forms({
        email: {
          value: '',
          type: 'email',
        },
        phoneNumber: {
          value: '',
          type: 'text',
        },
        mobileNumber: {
          value: '',
          type: 'text',
        },
      }),
      userSettingForm: new Forms({
        language: {
          value: '',
          type: 'select',
        },
      }),
      businessInformation: new Forms({
        businessRegistrationNumber: {
          value: '',
          type: 'text',
        },
        bankAccount: {
          value: '',
          type: 'text',
        },
        isInternational: {
          value: '',
          type: 'switch',
        },
        bankName: {
          value: '',
          type: 'text',
        },
        swift: {
          value: '',
          type: 'text',
        },
        iban: {
          value: '',
          type: 'text',
        },
        isVatRegistered: {
          value: '',
          type: 'switch',
        },
        isRegisteredBusinessEnterprise: {
          value: '',
          type: 'switch',
        },
        smallScaleManufacturer: {
          value: '',
          type: 'switch',
        },
        gs1number: {
          value: '',
          type: 'text',
        },
      }),
      userInformationForm: new Forms({
        userEmail: {
          value: '',
          type: 'email',
        },
        userFirstName: {
          value: '',
          type: 'text',
        },
        userLastName: {
          value: '',
          type: 'text',
        },
      }),
      passwordForm: new Forms({
        currentPassword: {
          value: '',
          type: 'password',
        },
        password: {
          value: '',
          type: 'password',
        },
        passwordConfirmation: {
          value: '',
          type: 'password',
        },
      }),
      addressForm: new Forms({
        careOf: {
          value: '',
          type: 'text',
        },
        streetName: {
          value: '',
          type: 'text',
        },
        secondStreetName: {
          value: '',
          type: 'text',
        },
        postalCode: {
          value: '',
          type: 'text', //'autocomplete',
         // remote: postalService,
        },
        cityName: {
          value: '',
          type: 'text',
        },
        countryId: {
          value: 1,
          type: 'select',
        },
      }),
      mainPublicProfileForm: new Forms({
        profileUrl: {
          value: '',
          type: 'text',
        },
        header: {
          value: '',
          type: 'text',
        },
        subheader: {
          value: '',
          type: 'text',
        },
        backgroundImage: {
          value: '',
          type: 'file',
        },
      }),
      defaultsPublicProfileForm: new Forms({
        appointmentConfirmationTitle: {
          value: '',
          type: 'text',
        },
        appointmentConfirmationTemplate: {
          value: '',
          type: 'textarea',
        },
        defaultReminderTitle: {
          value: '',
          type: 'text',
        },
        defaultReminder: {
          value: '',
          type: 'textarea',
        },
        defaultCancelMessageTitle: {
          value: '',
          type: 'text',
        },
        defaultCancelMessage: {
          value: '',
          type: 'textarea',
        },
      }),
      checkoutSettingsForm: new Forms({
        bookingRequirePayment: {
          value: '',
          type: 'checkbox',
        },
        bookingRequireAddress: {
          value: '',
          type: 'checkbox',
        },
      }),
      tabIndex: '1',
      activeNames: ['1', '2', '3'],
      activeProfileInformationName: ['11', '12', '13', '14', '15'],
      loading: false,
      stripe_sk: '',
      stripe_code: '',
      stripe_client_id: '',
      stripeButtonLoading: false,
    };
  },

  computed: {
    ...mapGetters({
      account: 'account',
      countries: 'allCountries',
      siteLanguages: 'allLanguages',
      municipalities: 'allMunicipalities',
      organizationTypes: 'allOrganizationTypes',
      postals: 'allPostals',
      publicProfile: 'publicProfile',
    }),

    publicPageDomain() {
      return process.env.PUBLIC_PAGE_DOMAIN;
    },

    isOnlineBooking() {
      return this.account.plan === 'Online Booking' || this.account.plan === 'Booking Pro';
    },
  },

  watch: {

    'businessInformation.isInternational': function (value) {
      if (value === true) {
        this.businessInformation.showField('bankName');
        this.businessInformation.showField('swift');
        this.businessInformation.showField('iban');
      } else {
        this.businessInformation.hideField('bankName');
        this.businessInformation.hideField('swift');
        this.businessInformation.hideField('iban');
      }
    },
    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} publicProfile    The publicProfile.
     */
    publicProfile(publicProfile) {
      this.mainPublicProfileForm.assignData(publicProfile);
      this.defaultsPublicProfileForm.assignData(publicProfile);
      this.checkoutSettingsForm.assignData(publicProfile);
      this.updateProfileUrl(publicProfile);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} postals The postals list.
     */
    postals(postals) {
      this.addressForm.setOptions('postalCode', postals.items.map(o => ({ name: o.postalCode })));
    },

    /**
     * Watch the value of the zipcode
     */
    'addressForm.postalCode': function (postalCode) {
      if (typeof postalCode === 'string') {
        if (postalCode.length > 3) {
          this.showCityByPostalCode(postalCode);
        }
      }

      if (typeof postalCode === 'object') {
        this.showCityByPostalCode(postalCode.name.toString());
        this.addressForm.postalCode = postalCode.name.toString();
      }
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} countries    The countries list.
     */
    countries(countries) {
      this.addressForm.setOptions('countryId', countries.items);
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} siteLanguages    The site languages list.
     */
    siteLanguages(siteLanguages) {
      this.userSettingForm.setOptions('language',
        siteLanguages.items.map(lng => ({
          id: lng.code,
          name: lng.name,
        })));
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} organizationTypes    The organization types list.
     */
    organizationTypes(organizationTypes) {
      this.organizationForm.setOptions('organizationTypeId',
        organizationTypes.map(organizationType => ({
          id: organizationType.id,
          name: this.$t(`layouts.header.${organizationType.name.toLowerCase()}`),
        })));
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} account    The account.
     */
    account(account) {
      this.businessInformation.assignData(account);
      this.contactInformationForm.assignData(account);
      this.userSettingForm.assignData(account);
      this.organizationForm.assignData(account);
      this.userInformationForm.assignData(account);
      this.assignOrganizationType();
      this.$i18n.locale = account.language;
    },

    /**
     * Watches organization type to hide or show registrationNumber.
     *
     * @param  {Integer} typeId The organization type id.
     */
    'organizationForm.organizationTypeId': function (typeId) {
      const type = this.organizationTypes.filter(t => (
        typeId.toString() === t.id.toString()
      ))[0];

      if (type.name === 'Company') {
        this.organizationForm.organizationDescription = this.account.organizationDescription;
        this.organizationForm.organizationName = this.account.organizationName;
        this.organizationForm.showField('organizationDescription');
        this.organizationForm.hideField('firstName');
        this.organizationForm.hideField('lastName');
      }

      if (type.name === 'Individual') {
        this.organizationForm.firstName = this.account.firstName;
        this.organizationForm.lastName = this.account.lastName;
        this.organizationForm.showField('firstName');
        this.organizationForm.showField('lastName');
        this.organizationForm.hideField('organizationDescription');
      }
    },
  },

  created() {
    this.businessInformation.hideField('bankName');
    this.businessInformation.hideField('swift');
    this.businessInformation.hideField('iban');

    if (!this.account.organizationName) {
      this.tabIndex = '2';
    }
    this.addressForm.setExtraAttr('postalCode', {
      remote: postalService,
    });

    this.mainPublicProfileForm.assignData(this.publicProfile);
    this.defaultsPublicProfileForm.assignData(this.publicProfile);
    this.checkoutSettingsForm.assignData(this.publicProfile);
    this.addressForm.assignData(this.account);
    this.businessInformation.assignData(this.account);
    this.contactInformationForm.assignData(this.account);
    this.userSettingForm.assignData(this.account);
    this.organizationForm.assignData(this.account);
    this.userInformationForm.assignData(this.account);

    Promise.all([
      countryService.all(),
      siteLanguagesService.all(),
      organizationTypeService.all(),
    ]).then(() => {
      this.assignOrganizationType();
      //this.addressForm.disableField('cityName');
      //this.addressForm.disableField('countryId');

      if (this.isOnlineBooking) {
        console.log('it is an online booking');
        if (Object.keys(this.publicProfile).length === 0) {
          console.log('there is a public profile');
          this.loading = true;
          publicProfileService.show()
            .then((profile) => {
              console.log('fetched the public profile');
              this.updateProfileUrl(profile);
              this.loading = false;
            }).catch((err) => {
              console.log('the error');
              console.log(err);
            });
        } else {
          this.updateProfileUrl(this.publicProfile);
        }
      }
    });

    Vue.$http.get('/settings', {
      params: {
        settings: ['stripe_sk', 'stripe_client_id'],
      },
    }).then((data) => {
      this.stripe_sk = data.data.stripe_sk;
      this.stripe_client_id = data.data.stripe_client_id;
      if (this.$route.query.code && !this.publicProfile.stripeConnect) {
        this.stripeButtonLoading = true;
        this.tabIndex = '6';
        this.stripe_code = this.$route.query.code;
        this.stripeUserGet();
      }
    });
  },

  methods: {
    stripeUserGet() {
      this.stripeButtonLoading = true;
      let href = window.location.href;
      href = href.substring(0, href.indexOf('?'));

      Vue.$http.post('/organization_settings/stripe_connect', {
        client_secret: this.stripe_sk,
        code: this.stripe_code,
        grant_type: 'authorization_code',
      }).then((data) => {
        this.publicProfile.stripeConnect = data.data;
        this.stripeButtonLoading = false;
        window.history.pushState({}, document.title, href);
        this.$message({
          message: 'Stripe successfully connected',
          type: 'success',
        });
      }).catch((error) => {
        this.stripeButtonLoading = false;
        window.history.pushState({}, document.title, href);
        this.$message({
          message: error.response.data.message,
          type: 'error',
        });
      });
    },

    /*
     * Updates the public profile form
     */
    updateProfileUrl(profile) {
      if (profile.profileUrl.length > 0) {
        this.mainPublicProfileForm.fields.profileUrl = 'hide';
        this.profileUrl = profile.profileUrl;
        this.$forceUpdate();
      }
    },
    /**
     * Method to assign organizationTypeId.
     */
    assignOrganizationType() {
      const type = this.organizationTypes.filter(organizationType =>
        (organizationType.name === this.account.organizationType));
      if (type.length === 1) {
        this.organizationForm.organizationTypeId = type[0].id;
      }
    },

    /**
     * Method to display the city
     */
    showCityByPostalCode(postalCode) {
      if (this.postals.items.length > 0) {
        const postal = this.postals.items.filter(p => (
          postalCode === p.postalCode.toString()
        ))[0];

        this.addressForm.cityName = postal.cityName;
      }
    },

    /**
     * This method will be called when saving the user information form.
     */
    saveUserInformation() {
      this.userInformationForm.loading = true;
      const account = accountTransformer.send(this.userInformationForm.data());
      accountService.updateUser(account).then(() => {
        this.userInformationForm.loading = false;
        this.$message({
          message: this.$t('static.accountInformation.accountSaveSuccess'),
          type: 'success',
        });
      })
        .catch((errors) => {
          this.userInformationForm.loading = false;
          this.userInformationForm.recordErrors(errors);
        });
    },

    /**
     * This method will be called when saving the form.
     */
    save() {
      this.addressForm.loading = true;
      this.businessInformation.loading = true;
      this.contactInformationForm.loading = true;
      this.userSettingForm.loading = true;
      this.organizationForm.loading = true;

      const account = accountTransformer.send(
        Object.assign({},
          this.addressForm.data(),
          this.businessInformation.data(),
          this.contactInformationForm.data(),
          this.userSettingForm.data(),
          this.organizationForm.data(),
        )
      );

      accountService.update(account).then(() => {
        this.addressForm.loading = false;
        this.businessInformation.loading = false;
        this.contactInformationForm.loading = false;
        this.userSettingForm.loading = false;
        this.organizationForm.loading = false;

        this.$message({
          message: this.$t('static.accountInformation.accountSaveSuccess'),
          type: 'success',
        });
      })
        .catch((errors) => {
          this.addressForm.loading = false;
          this.businessInformation.loading = false;
          this.contactInformationForm.loading = false;
          this.userSettingForm.loading = false;
          this.organizationForm.loading = false;

          this.addressForm.recordErrors(errors);
          this.businessInformation.recordErrors(errors);
          this.contactInformationForm.recordErrors(errors);
          this.userSettingForm.loading = false;
          this.organizationForm.recordErrors(errors);
        });
    },

    /**
     * This method will update the current password.
     */
    changePassword() {
      this.passwordForm.loading = true;
      authService.updatePassword(passwordTransformer.send(this.passwordForm.data()))
        .then(() => {
          this.passwordForm.loading = false;
          this.passwordForm.currentPassword = '';
          this.passwordForm.password = '';
          this.passwordForm.passwordConfirmation = '';
          this.$message({
            message: this.$t('static.accountInformation.passwordSaveSuccess'),
            type: 'success',
          });
        }).catch((errors) => {
          this.passwordForm.loading = false;
          this.passwordForm.recordErrors(passwordTransformer.fetch(errors));
        });
    },

    /**
     * This method will update the current password.
     */
    savePublicProfile() {
      this.defaultsPublicProfileForm.loading = true;
      this.mainPublicProfileForm.loading = true;
      this.checkoutSettingsForm.loading = true;
      const publicProfile = publicProfileTransformer.send(
        Object.assign({}, this.mainPublicProfileForm.data(),
          this.defaultsPublicProfileForm.data(),
          this.checkoutSettingsForm.data()),
      );
      publicProfileService.update(publicProfile)
        .then(() => {
          this.defaultsPublicProfileForm.loading = false;
          this.mainPublicProfileForm.loading = false;
          this.checkoutSettingsForm.loading = false;
          this.$message({
            message: this.$t('static.accountInformation.accountSaveSuccess'),
            type: 'success',
          });
        })
        .catch((errors) => {
          this.defaultsPublicProfileForm.loading = false;
          this.mainPublicProfileForm.loading = false;
          this.checkoutSettingsForm.loading = false;
          this.defaultsPublicProfileForm.recordErrors(errors);
          this.mainPublicProfileForm.recordErrors(errors);
          this.checkoutSettingsForm.recordErrors(errors);
        });
    },

    /**
     * This method will update the general information
     */
    saveGeneralInformation() {
      this.generalInformationForm.loading = true;
      const generalInformation = generalInformationTransformer.send();
    },
  },

  components: {
    VModal: require('./../../../../components/modal/modal.vue'),
    VForm: require('./../../../../components/form/form.vue'),
    VOpenHours: require('./../../../../components/openHours/openHours.vue'),
    VExceptionalHours: require('./../../../../components/exceptionalHours/exceptionalHours.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/profile/account/account.js